import { Form, Row, Col, Card, message } from "antd";
import { useEffect, useState } from "react";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber";
import CustomInput from "../../../components/CustomInput/Input";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import FormFooter from "../../../components/CustomForms/FormFooter";
import { BTN_TXT, ERROR_MSGS, CREDIT_LIMIT_MSGS } from "../../../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import {
    companyNameRules, formatFee, convertToPercentage, maxFundRequiredPctRules, percentToMaxFundRequiredPct
} from "../../../helpers/helpers";
import { getFinancingSubcategoryOptions } from "../../../helpers/dealCategoryProducts";
import {
    updateErrorStatus,
    getActiveBusinessList,
    getActiveEnterpriseList,
    addBusEnt,
    getBusEnt,
    getPlatformFeeData,
    getDealCategoryList,
    clearActiveEntList,
    clearActiveBusList,
} from "../../../redux/slices/creditLimitSlice";
import { count } from "../../../redux/slices/dealsSlice";
import { count as allCount } from "../../../redux/slices/AuthSlice";

const NewCreditLimitForm = ({ onClose, selectedStatus }) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [selectedPlatform, setSelectedPlatform] = useState(null);
    const { activeBusList, activeEntList, platformFeeData, dealCategoryList, error, entBusSubmitLoading } = useSelector(
        (state) => state.creditLimit
    );

    const dealCategoryId = Form.useWatch("dealCategoryId", form);
    const busName = Form.useWatch("busName", form);

    useEffect(() => {
        dispatch(getPlatformFeeData());
        dispatch(getDealCategoryList());
        return () => {
            dispatch(updateErrorStatus());
            dispatch(clearActiveBusList());
            dispatch(clearActiveEntList());
        };
    }, [dispatch]);

    useEffect(() => {
        if (platformFeeData?.length > 0) {
            const defaultPlatform = platformFeeData[0];
            form.setFieldsValue({
                platformFee: defaultPlatform.platformFeeId,
            });
            setSelectedPlatform(defaultPlatform);
        }
    }, [platformFeeData, form]);

    const handleDealCategoryChange = (dealCatId) => {
        form.setFieldsValue({ busName: undefined, entName: undefined });
        dispatch(clearActiveEntList());
        dispatch(clearActiveBusList());
        if (dealCatId) {
            dispatch(getActiveBusinessList(dealCatId));
        }
    };

    const handleBusinessChange = (orgId) => {
        form.setFieldsValue({ entName: undefined });
        dispatch(clearActiveEntList());
        if (orgId) {
            const currentDealCat = form.getFieldValue("dealCategoryId");
            dispatch(getActiveEnterpriseList({ orgId, dealCategoryId: currentDealCat }));
        }
    };

    const handleSubmit = async (values) => {
        const payload = {
            orgId: values.busName,
            entId: values.entName,
            dealCategoryId: values.dealCategoryId,
            previousCredit: values.prevCreditLimit,
            requestedCredit: values.requestedCreditLimit,
            platformFeeId: values.platformFee,
            maxFundRequiredPct: percentToMaxFundRequiredPct(values.maxFundRequiredPct),
        };

        const res = await dispatch(addBusEnt(payload));
        const businessName = activeBusList?.find((bus) => bus.orgId === values.busName)?.busName || "Business";
        const enterpriseName = activeEntList?.find((ent) => ent.entId === values.entName)?.entName || "Enterprise";

        if (res?.meta?.requestStatus === "fulfilled" && res?.payload?.statusCode === 201) {
            message.success(
                CREDIT_LIMIT_MSGS.RELATION_ADDED.replace("{business}", businessName).replace(
                    "{enterprise}",
                    enterpriseName,
                ),
            );
            form.resetFields();
            onClose();
            dispatch(getBusEnt(selectedStatus));
            dispatch(count("CREDITLIMIT"));
            dispatch(allCount("ALL"));
            return;
        }

        let rawError = null;
        if (res?.payload) {
            rawError = res.payload.message || res.payload.error || res.payload;
        }
        if (!rawError && res?.error) {
            rawError = res.error.message || res.error;
        }
        if (typeof rawError !== "string") {
            rawError = String(rawError || "").trim();
        }

        let errorMessage = rawError;
        if (!errorMessage) {
            errorMessage = CREDIT_LIMIT_MSGS.FAILED_ADD_RELATION.replace(
                "{business}",
                businessName,
            ).replace("{enterprise}", enterpriseName);
        } else if (errorMessage.includes("Business()") || errorMessage.includes("Enterprise()")) {
            errorMessage = errorMessage
                .replace(/Business\(\)/g, businessName)
                .replace(/Enterprise\(\)/g, enterpriseName)
                .replace(/\s+-\s+/g, " - ");
        }

        message.error(errorMessage);
    };

    return (
        <Form
            layout="vertical"
            form={form}
            onFinish={handleSubmit}
            initialValues={{ remember: true, platformFee: platformFeeData?.[0]?.platformFeeId }}
        >
            <Row gutter={[24, 0]}>
                <Col xs={24} md={12}>
                    <CustomSelect
                        label="Deal Category Type"
                        name="dealCategoryId"
                        showSearch
                        isRequired={true}
                        placeholder="Select Deal Category Type"
                        rules={[
                            { required: true, message: "Please select Deal Category Type" },
                        ]}
                        options={getFinancingSubcategoryOptions(dealCategoryList)}
                        onChange={handleDealCategoryChange}
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomSelect
                        label="Business Name"
                        name="busName"
                        showSearch
                        isRequired={true}
                        placeholder="Select your Business Name"
                        rules={companyNameRules("Business")}
                        options={activeBusList?.map((bus) => ({
                            label: bus.busName,
                            value: bus.orgId,
                        }))}
                        onChange={handleBusinessChange}
                        disabled={!dealCategoryId}
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomSelect
                        label="Enterprise Name"
                        name="entName"
                        showSearch
                        isRequired={true}
                        placeholder="Select your Enterprise Name"
                        rules={companyNameRules("Enterprise")}
                        options={activeEntList?.map((ent) => ({
                            label: ent.entName,
                            value: ent.entId,
                        }))}
                        disabled={!busName}
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomInputNumber
                        label="Previous Credit Limit (if any)"
                        name="prevCreditLimit"
                        type="text"
                        isRequired={true}
                        placeholder="Enter your Previous Credit Limit"
                        rules={[
                            { required: true, message: ERROR_MSGS.PRE_CRED_ERR_MSG },
                        ]}
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomInputNumber
                        label="Requested Credit Limit"
                        name="requestedCreditLimit"
                        type="text"
                        isRequired={true}
                        placeholder="Enter your Requested Credit Limit"
                        rules={[
                            { required: true, message: ERROR_MSGS.EXP_CRED_ERR_MSG },
                        ]}
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomInput
                        label="Max Fund Required (%)"
                        name="maxFundRequiredPct"
                        type="text"
                        isRequired={true}
                        placeholder="Enter Max Fund Required % (60-80)"
                        rules={maxFundRequiredPctRules}
                    />
                </Col>

                <Col xs={24} md={12}>
                    <CustomSelect
                        label="Platform Fee (%)"
                        name="platformFee"
                        isRequired={true}
                        placeholder="Select Platform Fee"
                        options={platformFeeData?.map((fee) => ({
                            label: convertToPercentage(fee.platformFee),
                            value: fee.platformFeeId,
                        }))}
                        onChange={(value) => {
                            form.setFieldsValue({ platformFee: value });
                            const platform = platformFeeData?.find((item) => item.platformFeeId === value) || null;
                            setSelectedPlatform(platform);
                        }}
                    />
                    {selectedPlatform && (
                        <Card
                            variant="outlined"
                            style={{
                                background: "#f5faff",
                                borderRadius: "6px",
                                minHeight: "10%",
                            }}
                        >
                            {[
                                { label: "Platform ID", value: selectedPlatform.platformFeeId },
                                { label: "Platform Fees", value: formatFee(selectedPlatform.platformFee, "percent") },
                                { label: "Stamp Fees", value: formatFee(selectedPlatform.stampFee, "amount") },
                                { label: "Penal Charges", value: formatFee(selectedPlatform.penalCharge, "percent") },
                                {
                                    label: "Miscellaneous Fees",
                                    value: formatFee(selectedPlatform.miscellaneousFees, "amount"),
                                },
                            ].map((item, index) => (
                                <div
                                    key={index}
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr auto 1fr",
                                        marginBottom: "6px",
                                    }}
                                >
                                    <span style={{ fontWeight: 600 }}>{item.label}</span>
                                    <span style={{ textAlign: "center" }}>:</span>
                                    <span style={{ textAlign: "right" }}>{item.value}</span>
                                </div>
                            ))}
                        </Card>
                    )}
                </Col>
            </Row>

            <FormFooter
                onCancelClick={() => {
                    onClose();
                    form.resetFields();
                }}
                submitLoading={entBusSubmitLoading}
                btnName={BTN_TXT.SUBMIT}
                error={error}
            />
        </Form>
    );
};

export default NewCreditLimitForm;
